import { DatePipe } from '@angular/common';
import { AddDocumentComponent } from '@/app/atoms/add-document/add-document.component';
import { RecruitGroupResponseDto, RecruitGroupTypesEnum } from '@/app/common/general-types/recruit-groups.dto';
import { ScreeningListCreatorItem } from '@/app/common/general-types/social-screening.dto';
import { RecruitGroupService } from '@/app/services/recruit-group/recruit-group.service';
import { S3uploaderService } from '@/app/services/s3uploader/s3uploader.service';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { CreateMonitoring, MonitoringCreatorSourceMethod } from '../../services/create-monitoring';

interface ManualCreatorRow {
  creatorName: string;
  instagram: string;
  tiktok: string;
}

@Component({
  selector: 'cmb-monitoring-creators',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AddDocumentComponent, DatePipe],
  templateUrl: './monitoring-creators.html',
  styleUrl: './monitoring-creators.scss',
})
export class MonitoringCreators implements OnInit {

  readonly methods: { id: MonitoringCreatorSourceMethod; name: string; description: string }[] = [
    { id: 'one', name: 'Add one by one', description: 'Enter creator handles or names manually.' },
    { id: 'csv', name: 'Upload CSV file', description: 'Bulk-import a list of creators from a spreadsheet.' },
    { id: 'shout', name: 'Select from SHOUT', description: 'Use a creator group you already have.' },
    { id: 'later', name: 'Add creators later', description: 'Submit the brief now and add creators any time.' },
  ];

  readonly groups = signal<RecruitGroupResponseDto[]>([]);
  readonly rows = signal<ManualCreatorRow[]>([]);

  public readonly createMonitoringService = inject(CreateMonitoring);
  private readonly recruitGroupService = inject(RecruitGroupService);
  private readonly uploadService = inject(S3uploaderService);

  readonly selectedMethod = computed(() => this.createMonitoringService.creatorSourceMethod());

  readonly tally = computed<number>(() => {
    switch (this.selectedMethod()) {
      case 'one':
        return this.createMonitoringService.manualCreators().length;
      case 'shout':
        return this.createMonitoringService.selectedList()?.numberOfCreators
          ?? this.createMonitoringService.selectedList()?.creators?.length
          ?? 0;
      default:
        return 0;
    }
  });

  ngOnInit(): void {
    void this.recruitGroupService.initRecruitmentGroups().then(() => {
      this.groups.set(
        this.recruitGroupService.visibleGroups().filter(
          (group) => group.group_type !== RecruitGroupTypesEnum.CREATOR_INTEREST,
        ),
      );
    });
  }

  selectMethod(method: MonitoringCreatorSourceMethod): void {
    this.createMonitoringService.creatorSourceMethod.set(method);
    if (method !== 'shout') {
      this.createMonitoringService.selectedList.set(undefined);
    }
    if (method !== 'csv') {
      this.createMonitoringService.uploadedCsvUrl.set(undefined);
    }
    if (method !== 'one') {
      this.rows.set([]);
      this.createMonitoringService.manualCreators.set([]);
    } else if (this.rows().length === 0) {
      this.rows.set([{ creatorName: '', instagram: '', tiktok: '' }]);
    }
  }

  selectGroup(group: RecruitGroupResponseDto): void {
    this.createMonitoringService.selectedList.set(group);
  }

  onDocumentUploaded(event: { file: string; fileKey: string; fullUrl: string }): void {
    this.createMonitoringService.uploadedCsvUrl.set(event.fullUrl);
  }

  getTemplate(): void {
    void this.uploadService.secureCreatorCsvDownload();
  }

  addRow(): void {
    this.rows.update((rows) => [...rows, { creatorName: '', instagram: '', tiktok: '' }]);
  }

  removeRow(index: number): void {
    this.rows.update((rows) => rows.filter((_, rowIndex) => rowIndex !== index));
    this.syncManualCreators();
  }

  updateRow(index: number, field: keyof ManualCreatorRow, value: string): void {
    this.rows.update((rows) => {
      const updated = [...rows];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    this.syncManualCreators();
  }

  private syncManualCreators(): void {
    const creators: ScreeningListCreatorItem[] = this.rows()
      .filter((row) => this.normaliseHandle(row.instagram) || this.normaliseHandle(row.tiktok))
      .map((row) => ({
        name: row.creatorName.trim() || `@${this.normaliseHandle(row.instagram) || this.normaliseHandle(row.tiktok)}`,
        instagramHandle: this.normaliseHandle(row.instagram) || undefined,
        tiktokHandle: this.normaliseHandle(row.tiktok) || undefined,
      }));
    this.createMonitoringService.manualCreators.set(creators);
  }

  private normaliseHandle(value: string): string {
    return value.trim().replace(/^@/, '');
  }
}
